import { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import GoogleAPI from '../utils/GoogleAPI';

/**
 * SpreadSheetシート名定数
 */
/** 販売数量・販売単価変更 */
export const SHEET_NAME_QUANTITY_AND_PRICE = "QuantityAndPrice";
/** 設備・人員構成変更 */
export const SHEET_NAME_EQUIPMENT_STAFFING = "EquipmentStaffingReconfiguration";
/** 段階的設備投資 */
export const SHEET_NAME_PHASED_CAPITAL_INVESTMENT = "PhasedCapitalInvestment";
/** 稼働日数変更 */
export const SHEET_NAME_OPERATING_DAYS = "OperatingDays";
/** 稼働率改善 */
export const SHEET_NAME_DOWNTIME = "Downtime";
/** 歩留改善 */
export const SHEET_NAME_YIELD_IMPROVEMENT = "YieldImprovement";
/** サイクルタイム短縮 */
export const SHEET_NAME_CYCLE_TIME_REDUCTION = "CycleTimeReduction";
/** 既存ライン生産数量（生産キャパグラフ） */
export const SHEET_NAME_EXISTING_LINE_QUANTITY = "ExistingLineQuantity";

/**
 * SpreadSheet同期の設定
 */
export interface SpreadSheetSyncConfig<T> {
  /** フォルダパス（例: "SpreadSheet/WorkingDays"） */
  folderPath: string;
  /** ファイル名 */
  fileName: string;
  /** 共有ドライブ名（省略時はマイドライブ） */
  sharedDriveName?: string | null;
  /** コンポーネントデータ → SpreadSheetデータへの変換関数 */
  toSpreadSheetData: (data: T) => any[][];
  /** SpreadSheetデータ → コンポーネントデータへの変換関数 */
  fromSpreadSheetData: (values: any[][]) => T;
  /** データ取得範囲（例: "SheetData!B2:M"） */
  getRange: (sheetName: string) => string;
  /** true: 同名の既存ファイルを削除してから新規作成する（デフォルト: false = 既存ファイルを再利用） */
  recreateFile?: boolean;
  /** true: シート名を GoogleAPI.DEFAULT_SHEET_NAME 固定として扱う（デフォルト: false = getFirstSheetName で動的取得） */
  useFixedSheetName?: boolean;
  /** 値書き込み完了後に呼ばれる任意のコールバック（書式・入力規則・条件付き書式等の追加設定用） */
  onAfterWrite?: (spreadsheetId: string, sheetId: number) => Promise<void>;
}

/**
 * SpreadSheet同期の状態
 */
export interface SpreadSheetSyncState {
  /** ローディング中かどうか */
  isLoading: boolean;
  /** 入力モード（"manual" | "google"） */
  inputMode: "manual" | "google";
  /** アクティブなSpreadSheetのID */
  activeSpreadsheetId: string | null;
}

/**
 * SpreadSheet同期の結果
 */
export interface SpreadSheetSyncResult<T> {
  /** 同期状態 */
  state: SpreadSheetSyncState;
  /** GoogleSpreadボタン押下時のハンドラ */
  handleGoogleSpread: (data: T) => Promise<void>;
  /** データ連携ボタン押下時のハンドラ */
  handleSyncData: () => Promise<T | null>;
  /** 手動入力モードに切り替え */
  handleManualClick: () => void;
  /** クリーンアップ（モーダル閉じる時など） */
  cleanup: () => void;
  /** 入力モードを設定 */
  setInputMode: (mode: "manual" | "google") => void;
}

/**
 * SpreadSheet同期用カスタムフック
 *
 * @description
 * WorkingDays、NonWorkingHours、その他SpreadSheet連携が必要なコンポーネントで
 * 共通のSpreadSheet同期ロジックを提供する。
 *
 * @example
 * ```typescript
 * const { state, handleGoogleSpread, handleSyncData, cleanup } = useSpreadSheetSync({
 *   folderPath: "SpreadSheet/WorkingDays",
 *   fileName: "稼働日数",
 *   toSpreadSheetData: (data) => convertToSheetData(data),
 *   fromSpreadSheetData: (values) => convertFromSheetData(values),
 *   getRange: (sheetName) => `${sheetName}!B2:M`,
 * });
 * ```
 */
export const useSpreadSheetSync = <T>(
  config: SpreadSheetSyncConfig<T>,
  googleAPI: GoogleAPI,
  toast: (options: { title: string; description: React.ReactNode; status: 'error' | 'warning' | 'success' | 'info'; duration: number; isClosable: boolean }) => void
): SpreadSheetSyncResult<T> => {
  const { t } = useTranslation();

  // 状態管理
  const [isLoading, setIsLoading] = useState(false);
  const [inputMode, setInputMode] = useState<"manual" | "google">("manual");
  const [activeSpreadsheetId, setActiveSpreadsheetId] = useState<string | null>(null);

  // SpreadSheetウィンドウの参照
  const spreadSheetWindowRef = useRef<Window | null>(null);

  // ウィンドウ監視用インターバルの参照
  const windowCheckIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  /**
   * SpreadSheetウィンドウを閉じる
   */
  const closeSpreadSheetWindow = useCallback(() => {
    if (spreadSheetWindowRef.current) {
      spreadSheetWindowRef.current.close();
      spreadSheetWindowRef.current = null;
    }
    if (windowCheckIntervalRef.current) {
      clearInterval(windowCheckIntervalRef.current);
      windowCheckIntervalRef.current = null;
    }
  }, []);

  /**
   * アンマウント時（画面遷移等）にSpreadSheetウィンドウを閉じ、監視インターバルを停止する
   */
  useEffect(() => {
    return () => {
      closeSpreadSheetWindow();
    };
  }, [closeSpreadSheetWindow]);

  /**
   * 手動入力モードに切り替え
   */
  const handleManualClick = useCallback(() => {
    closeSpreadSheetWindow();
    setInputMode("manual");
  }, [closeSpreadSheetWindow]);

  /**
   * クリーンアップ処理
   */
  const cleanup = useCallback(() => {
    closeSpreadSheetWindow();
    setInputMode("manual");
    setActiveSpreadsheetId(null);
  }, [closeSpreadSheetWindow]);

  /**
   * GoogleSpreadボタン押下時のハンドラ
   */
  const handleGoogleSpread = useCallback(async (data: T) => {
    if (inputMode === "google") {
      return;
    }

    try {
      setIsLoading(true);

      // ポップアップブロック回避：非同期処理より前にユーザー操作起因でウィンドウを確保
      const newWindow = window.open("", "_blank");

      // Googleアカウント認証
      if (!await googleAPI.signIn()) {
        throw new Error("signIn error");
      }

      // フォルダ作成
      let folderId = await googleAPI.createFolderPath(config.folderPath, config.sharedDriveName ?? null);
      if (!folderId) {
        folderId = "";
      }

      // 既存ファイル削除→再作成（recreateFile指定時のみ。常に1ファイルを維持したい画面向け）
      if (config.recreateFile) {
        const existingFileId = await googleAPI.getFileOrFolderId(config.fileName, "file", config.sharedDriveName ?? null, folderId);
        if (existingFileId) {
          await googleAPI.deleteFile(existingFileId);
        }
      }

      const spreadsheetId = await googleAPI.createSpreadSheet(config.fileName, folderId, config.sharedDriveName ?? null);
      setActiveSpreadsheetId(spreadsheetId);

      // 事前確保したウィンドウをスプレッドシートに遷移
      if (newWindow) {
        newWindow.location.href = googleAPI.getOpenSpradSheetUrl(spreadsheetId ?? "");
        if (spreadsheetId) {
          spreadSheetWindowRef.current = newWindow;
        }

        // GoogleSpreadシートウィンドウを手動で閉じられた場合
        windowCheckIntervalRef.current = setInterval(() => {
          if (newWindow.closed) {
            if (windowCheckIntervalRef.current) {
              clearInterval(windowCheckIntervalRef.current);
              windowCheckIntervalRef.current = null;
            }
            setInputMode("manual");
          }
        }, 500); // 500ms ごとに監視
      } else {
        console.error("GoogleSpreadシートがブロックされました");
      }

      // シートにデータを書き込み
      const sheetData = config.toSpreadSheetData(data);
      const sheetName = config.useFixedSheetName
        ? GoogleAPI.DEFAULT_SHEET_NAME
        : await googleAPI.getFirstSheetName(spreadsheetId) ?? GoogleAPI.DEFAULT_SHEET_NAME;
      await googleAPI.spreadSheetSetValue(spreadsheetId, sheetName, 1, 1, sheetData);

      // 値書き込み後の追加設定（書式・入力規則・条件付き書式等）
      if (config.onAfterWrite) {
        const sheetId = config.useFixedSheetName
          ? await googleAPI.getFirstSheetId(spreadsheetId)
          : null;
        await config.onAfterWrite(spreadsheetId, sheetId ?? 0);
      }

      setInputMode("google");

    } catch (error) {
      console.error("Change GoogleSpread Error.", error);
      closeSpreadSheetWindow();
      setInputMode("manual");

      toast({
        title: t('common.label.error'),
        description: t('common.message.spreadsheetUnavailable'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  }, [config, googleAPI, inputMode, closeSpreadSheetWindow, toast]);

  /**
   * データ連携ボタン押下時のハンドラ
   */
  const handleSyncData = useCallback(async (): Promise<T | null> => {
    setIsLoading(true);
    try {
      if (!activeSpreadsheetId) {
        console.error("スプレッドシートIDがありません");
        return null;
      }

      // アクセストークンリフレッシュ
      if (!await googleAPI.ensureValidToken()) {
        await googleAPI.signIn();
      }

      // シート名を取得（固定名オプション指定時は動的取得をスキップ）
      const sheetName = config.useFixedSheetName
        ? GoogleAPI.DEFAULT_SHEET_NAME
        : await googleAPI.getFirstSheetName(activeSpreadsheetId) ?? GoogleAPI.DEFAULT_SHEET_NAME;

      // 範囲を取得
      const range = config.getRange(sheetName);

      // スプレッドシートから値を取得
      const result = await googleAPI.spreadSheetGetValues(activeSpreadsheetId, range);

      // データを変換して返す
      const convertedData = config.fromSpreadSheetData(result.values ?? []);
      return convertedData;

    } catch (error) {
      console.error("データ連携エラー:", error);
      toast({
        title: t('common.label.error'),
        description: t('common.message.syncFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [activeSpreadsheetId, config, googleAPI, toast]);

  return {
    state: {
      isLoading,
      inputMode,
      activeSpreadsheetId,
    },
    handleGoogleSpread,
    handleSyncData,
    handleManualClick,
    cleanup,
    setInputMode,
  };
};

export default useSpreadSheetSync;
